import { FormInstance, message } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { CalibrationFileUploadMetadata } from 'pages/reporting/reports/raut/components/CalibrationFileUpload'
import {
  CalibrationPageData,
  calibrationPageDataSchema,
} from 'pages/reporting/reports/raut/rautReportData'
import { mapParsedNdeToCalibration } from 'pages/reporting/reports/raut/utils/nde/mapParsedNdeToCalibration'
import NdeCalibrationWorker from 'pages/reporting/reports/raut/utils/nde/ndeCalibration.worker?worker'
import {
  NdeCalibrationWorkerResult,
  ParsedCalibrationAutofill,
} from 'pages/reporting/reports/raut/utils/nde/types'
import { normalizeCalibrationFormData } from 'pages/reporting/reports/raut/utils/normalizeCalibrationFormData'
import { useCallback, useEffect, useState } from 'react'
import { GeckoMeasurementUnit } from 'types'

interface UseCalibrationNdeUploadProps {
  metadataEntryId: string
  calibrationData: CalibrationPageData
  form: FormInstance<CalibrationPageData>
  geometricUnit: GeckoMeasurementUnit
  distanceUnit: GeckoMeasurementUnit
  hasPhasedArrayInspection: boolean
  saveCalibrationSilent: (
    values: Partial<CalibrationPageData>,
    isComplete: boolean,
    deleteData?: string[]
  ) => Promise<void>
}

interface UseCalibrationNdeUploadResult {
  isParsing: boolean
  isUploading: boolean
  parseError?: string
  metadata?: CalibrationFileUploadMetadata
  uploadCalibrationNde: (file: File) => Promise<void>
  deleteCalibrationNde: () => Promise<void>
}

const sanitizeFileName = (value: string): string => {
  return value.replace(/[^A-Za-z0-9._-]+/g, '-')
}

const CALIBRATION_DATA_FILE_DELETE_PATH =
  'section_info.calibration_page.calibrationDataFile'

const buildUploadPath = (fileName: string): string => {
  return `calibration/nde/${sanitizeFileName(fileName)}`
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  return error instanceof Error ? error.message : fallback
}

const getUploadPathForMetadata = (
  fileMetadata?: CalibrationFileUploadMetadata
): string | undefined => {
  if (!fileMetadata?.originalName) {
    return undefined
  }

  return buildUploadPath(fileMetadata.originalName)
}

const parseFileInWorker = (file: File): Promise<ParsedCalibrationAutofill> => {
  return new Promise((resolve, reject) => {
    const worker = new NdeCalibrationWorker()

    worker.onmessage = (event: MessageEvent<NdeCalibrationWorkerResult>) => {
      worker.terminate()
      if (event.data.type === 'success') {
        resolve(event.data.result)
        return
      }

      reject(new Error(event.data.error))
    }

    worker.onerror = (event) => {
      worker.terminate()
      reject(
        new Error(event.message || 'Worker failed while parsing the NDE file.')
      )
    }

    worker.postMessage(file)
  })
}

export const useCalibrationNdeUpload = ({
  metadataEntryId,
  calibrationData,
  form,
  geometricUnit,
  distanceUnit,
  hasPhasedArrayInspection,
  saveCalibrationSilent,
}: UseCalibrationNdeUploadProps): UseCalibrationNdeUploadResult => {
  const { rolodexDeleteFile, rolodexUploadFile } = useRolodexEdit()
  const [isParsing, setIsParsing] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [parseError, setParseError] = useState<string>()
  const [metadata, setMetadata] = useState<CalibrationFileUploadMetadata>()

  useEffect(() => {
    setMetadata(calibrationData.calibrationDataFile)
  }, [calibrationData.calibrationDataFile])

  const deleteCalibrationNde = useCallback(async () => {
    const currentValues = form.getFieldsValue(true) as CalibrationPageData
    const currentFile = currentValues.calibrationDataFile
    const nextCalibrationData: CalibrationPageData = {
      ...currentValues,
      calibrationDataFile: undefined,
    }

    form.setFieldsValue(nextCalibrationData)
    setMetadata(undefined)
    setParseError(undefined)
    setIsUploading(true)

    try {
      await saveCalibrationSilent(
        nextCalibrationData,
        calibrationPageDataSchema.safeParse(nextCalibrationData).success,
        [CALIBRATION_DATA_FILE_DELETE_PATH]
      )

      const persistedUploadPath = getUploadPathForMetadata(currentFile)
      if (persistedUploadPath) {
        try {
          await rolodexDeleteFile(metadataEntryId, persistedUploadPath)
        } catch (error) {
          console.error(
            'Failed to delete calibration file from Rolodex:',
            error
          )
          if (!currentFile?.uploadWarning) {
            message.warning(
              'Calibration file metadata was removed, but deleting the Rolodex file failed'
            )
            return
          }
        }
      }

      message.success('Calibration file removed')
    } catch (error) {
      form.setFieldsValue(currentValues)
      setMetadata(currentFile)
      console.error('Failed to remove calibration file:', error)
      message.error('Failed to remove calibration file')
    } finally {
      setIsUploading(false)
    }
  }, [form, metadataEntryId, rolodexDeleteFile, saveCalibrationSilent])

  const uploadCalibrationNde = useCallback(
    async (file: File) => {
      if (!file.name.toLowerCase().endsWith('.nde')) {
        setParseError('Only .nde files are supported for calibration autofill.')
        message.error('Only .nde files are supported for calibration autofill')
        return
      }

      setParseError(undefined)
      setIsParsing(true)

      let parsed: ParsedCalibrationAutofill
      try {
        parsed = await parseFileInWorker(file)
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          'Failed to parse the selected calibration NDE file.'
        )
        setParseError(errorMessage)
        message.error(errorMessage)
        setIsParsing(false)
        return
      }

      const currentValues = form.getFieldsValue(true) as CalibrationPageData
      const mergedCalibration = mapParsedNdeToCalibration(
        currentValues,
        parsed,
        geometricUnit
      )
      const normalizedCalibration = normalizeCalibrationFormData(
        mergedCalibration,
        {
          geometricUnit,
          distanceUnit,
          hasPhasedArrayInspection,
        }
      )

      const nextCalibrationData: CalibrationPageData = {
        ...normalizedCalibration,
        calibrationDataFile: {
          originalName: file.name,
          parseWarnings: parsed.warnings,
          uploadWarning: undefined,
        },
      }

      form.setFieldsValue(nextCalibrationData)
      setMetadata(nextCalibrationData.calibrationDataFile)

      const parseSaveComplete =
        calibrationPageDataSchema.safeParse(nextCalibrationData).success
      try {
        await saveCalibrationSilent(nextCalibrationData, parseSaveComplete)
      } catch (error) {
        form.setFieldsValue(currentValues)
        setMetadata(currentValues.calibrationDataFile)
        console.error('Failed to save parsed calibration values:', error)
        message.error(
          getErrorMessage(error, 'Failed to save parsed calibration values.')
        )
        return
      } finally {
        setIsParsing(false)
      }

      setIsUploading(true)
      const uploadPath = buildUploadPath(file.name)
      const previousUploadPath = getUploadPathForMetadata(
        currentValues.calibrationDataFile
      )
      try {
        await rolodexUploadFile(metadataEntryId, file, uploadPath)
        if (previousUploadPath && previousUploadPath !== uploadPath) {
          try {
            await rolodexDeleteFile(metadataEntryId, previousUploadPath)
          } catch (error) {
            console.error(
              'Failed to delete previous calibration file from Rolodex:',
              error
            )
            if (!currentValues.calibrationDataFile?.uploadWarning) {
              message.warning(
                'Calibration NDE uploaded, but deleting the previous Rolodex file failed'
              )
              return
            }
          }
        }

        if (parsed.warnings.length > 0) {
          message.warning('Calibration NDE parsed with warnings')
        } else {
          message.success('Calibration NDE parsed and autofill applied')
        }
      } catch (error) {
        const warning = getErrorMessage(
          error,
          'Rolodex upload failed after local autofill completed.'
        )

        const warningCalibrationData: CalibrationPageData = {
          ...nextCalibrationData,
          calibrationDataFile: {
            ...nextCalibrationData.calibrationDataFile,
            uploadWarning: warning,
          },
        }

        setMetadata(warningCalibrationData.calibrationDataFile)
        try {
          await saveCalibrationSilent(
            warningCalibrationData,
            calibrationPageDataSchema.safeParse(warningCalibrationData).success
          )
        } catch (saveError) {
          console.error('Failed to save calibration upload warning:', saveError)
          message.error(
            'Calibration values were applied, but the upload warning could not be saved'
          )
          return
        }

        if (previousUploadPath && previousUploadPath !== uploadPath) {
          try {
            await rolodexDeleteFile(metadataEntryId, previousUploadPath)
          } catch (cleanupError) {
            console.error(
              'Failed to delete previous calibration file after upload failure:',
              cleanupError
            )
          }
        }

        message.warning(
          'Calibration values were applied, but the Rolodex upload failed'
        )
      } finally {
        setIsUploading(false)
      }
    },
    [
      distanceUnit,
      form,
      geometricUnit,
      hasPhasedArrayInspection,
      metadataEntryId,
      rolodexDeleteFile,
      rolodexUploadFile,
      saveCalibrationSilent,
    ]
  )

  return {
    isParsing,
    isUploading,
    parseError,
    metadata,
    uploadCalibrationNde,
    deleteCalibrationNde,
  }
}
